'use client';
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { calculateCostRange } from '@shared/utils/helpers/formatNumbers';
import {
  AlertCircle,
  Bot,
  Briefcase,
  Building,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  EyeOff,
  Trash,
  User
} from 'lucide-react';
import Link from 'next/link';
import { type ReactNode, useState } from 'react';

import Avatar from '../common/Avatar';
import ProductImage from '../products/ProductImage';

export type BorderState =
  | 'none'
  | 'editing'
  | 'ready'
  | 'removed'
  | 'incomplete';

const borderClasses: Record<BorderState, string> = {
  none: '',
  editing: 'border-2 border-white',
  ready: 'border-2 border-purple-500',
  removed: 'border-2 border-destructive',
  incomplete: 'border-2 border-white/50'
};

// Only the fields this card renders. Narrowed from the whole `FactiiiTag` so a
// caller holding a plain factiii row (the vault's Factiiis tab) can reuse the
// card without inventing postFactiiis, spaceTimes and uploads it never reads.
// A full FactiiiTag still satisfies it.
export type FactiiiCardTag = Pick<
  FactiiiTag,
  | 'id'
  | 'factiiiId'
  | 'name'
  | 'description'
  | 'avatarKey'
  | 'spaceSlug'
  | 'isRetired'
  | 'award'
  // sourceIcon draws the source-type badges off these two.
  | 'requirements'
  | 'isBot'
>;

type Props = {
  disabled?: string;
  tag?: FactiiiCardTag;
  replyConfig?: RouterOutputs['search']['factiiis']['factiiis'][number]['replyConfig'];
  handleClick: () => void;
  borderState?: BorderState;
  trashHandler?: () => void;
  // Extra controls at the row's end, before the trash icon. Clicks inside do
  // not reach the card, so a button here never doubles as a card click.
  actions?: ReactNode;
  // No drop shadow: in a stacked list on a dark surface the shadows read as
  // grey bars under every row rather than as lift.
  flat?: boolean;
  // Selected-list mode: description + inputs are hidden behind a caret to keep
  // the row short; `children` is the expanded content (inputs / award fields).
  collapsible?: boolean;
  children?: ReactNode;
  // Unmet requirement descriptions for this tag. Rendered at the bottom of the
  // card (issues only) so the user sees exactly what this factiii still needs,
  // without an (i) modal that mixed every factiii's requirements together.
  missing?: string[];
};

// One card for everything: search results, the awards list, and the selected
// (Newly/Already added) list. Awards are parsed into a FactiiiTag (avatar image
// in avatarKey, cost in description) so they render through this exact path.
export const FactiiiSearchResult = ({
  disabled,
  tag,
  replyConfig,
  handleClick,
  borderState = 'none',
  trashHandler,
  actions,
  flat,
  collapsible,
  children,
  missing
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  const formatSlug = (factiii: FactiiiCardTag) => {
    if (factiii.spaceSlug) {
      return `s/${factiii.spaceSlug}`;
    }
    return '';
  };

  if (!tag) return null;

  const isAward = !!tag.award;
  const isDisabled = !isAward && !!(tag.isRetired || disabled);
  const description = tag.description;
  const showDetails = !collapsible || expanded;
  const canExpand = collapsible && (!!children || !!description);

  const handleCardClick = () => {
    if (collapsible) {
      if (canExpand) setExpanded((e) => !e);
    } else {
      handleClick();
    }
  };

  return (
    <div
      className={`relative flex flex-col rounded-lg bg-background-secondary ${flat ? '' : 'shadow-lg'} ${collapsible ? 'p-2' : 'p-3'} ${borderClasses[borderState]} ${isDisabled ? 'pointer-events-none opacity-60 grayscale' : 'cursor-pointer'}`}
      onClick={handleCardClick}
    >
      {!isAward && tag?.isRetired && (
        <div className="absolute right-3 top-3 z-10 rounded-md bg-amber-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
          Retired
        </div>
      )}
      {!isAward && disabled && (
        <div className="absolute right-3 top-3 z-10" title={disabled}>
          <div className="rounded-md bg-primary/80 px-2 py-0.5 text-xs font-semibold text-white shadow-lg">
            Already Added
          </div>
        </div>
      )}

      <div className={`flex items-center ${collapsible ? 'gap-2' : 'gap-3'}`}>
        {isAward ? (
          <ProductImage
            imageKey={tag.avatarKey}
            alt={tag.name}
            size={collapsible ? 30 : 50}
            round
          />
        ) : (
          <Avatar
            factiiiAvatarKey={tag.avatarKey ?? null}
            factiiiId={tag.id}
            size={collapsible ? 30 : 50}
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            {!isAward && sourceIcon(tag)}
            <span className="truncate font-emphasis">{tag.name}</span>
            {!isAward && tag.isRetired && (
              <span className="text-xs text-textSecondary">(Retired)</span>
            )}
            {!isAward && tag.spaceSlug && (
              <span className="text-sm text-textSecondary">
                {formatSlug(tag)}
              </span>
            )}
            {!isAward && (
              <Link
                href={`/fids/${tag.factiiiId}`}
                className="hover:text-primary"
                title={`Visit ${tag.name} factiii`}
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={14} />
              </Link>
            )}
          </div>
          {showDetails && description && (
            <div className="truncate text-sm text-textSecondary/70">
              {description}
            </div>
          )}
          {/* Award cost — shown like the bot cost line, but always visible
              (even collapsed) so a staged award's price is never hidden. */}
          {isAward && tag.award && (
            <div className="mt-1 flex items-center gap-1 text-xs text-textSecondary">
              <span>
                Costs {tag.award.cost.toLocaleString()} trons
                {tag.award.count > 1 &&
                  ` each · ${(tag.award.cost * tag.award.count).toLocaleString()} total`}
              </span>
            </div>
          )}
          {!isAward && replyConfig && !tag?.isRetired && showDetails && (
            <div className="mt-1 flex items-center gap-2 text-xs text-textSecondary">
              <span className="flex items-center gap-1">
                Costs {calculateCostRange(replyConfig)} trons
              </span>
            </div>
          )}
          {!isAward && tag?.isRetired && showDetails && (
            <p className="mt-1 text-xs text-textSecondary">
              This factiii has been retired and cannot be used
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {actions && (
            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
          {canExpand && (
            <button
              className="rounded-md p-1 text-textSecondary hover:text-textPrimary"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((x) => !x);
              }}
            >
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
          {trashHandler && !disabled && !tag?.isRetired && (
            <button
              className="rounded-md p-1 text-destructive transition-colors hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                trashHandler();
              }}
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      </div>

      {collapsible && expanded && children && (
        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}

      {/* Only what this factiii still needs — issues, not a full checklist. */}
      {missing && missing.length > 0 && (
        <div className="mt-2 flex flex-col gap-0.5 border-t border-ds-border-subtle pt-2">
          {missing.map((m) => (
            <span
              key={m}
              className="flex items-center gap-1 text-xs text-destructive"
            >
              <AlertCircle size={12} className="shrink-0" /> {m}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const sourceIcon = (source: FactiiiCardTag) => {
  return (
    <div>
      {source.requirements.includes('ANONYMOUS_SOURCE') && (
        <span title="Anonymous Source">
          <EyeOff className="size-4 text-textSecondary" />
        </span>
      )}
      {source.requirements.includes('GOVERNMENT_SOURCE') && (
        <span title="Government Source">
          <Building className="size-4 text-textSecondary" />
        </span>
      )}
      {source.requirements.includes('ENTERPRISE_SOURCE') && (
        <span title="Enterprise Source">
          <Briefcase className="size-4 text-textSecondary" />
        </span>
      )}
      {source.requirements.includes('HUMAN_SOURCE') && (
        <span title="Human Source">
          <User className="size-4 text-textSecondary" />
        </span>
      )}
      {source.isBot && (
        <span title="Bot Factiii">
          <Bot className="size-4 text-textSecondary" />
        </span>
      )}
    </div>
  );
};
